import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import { questions, maxScore } from '@/data/questions'
import { getCategoryScores, getTier } from '@/lib/scoring'
import type { LeadInfo } from '@/lib/store'

export interface SubmissionResult {
  _id?: string
  ok: boolean
  error?: string
}

/**
 * Persists a quiz submission (lead info + answers + computed score/tier)
 * into the `quiz_submissions` MongoDB collection via the Lumi SDK.
 */
export function useQuizSubmission() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (
    lead: LeadInfo,
    answers: Record<string, number>
  ): Promise<SubmissionResult> => {
    setSubmitting(true)
    setError(null)

    try {
      // Compute score & tier
      const totalScore = Object.values(answers).reduce(
        (sum, v) => sum + (typeof v === 'number' ? v : 0),
        0
      )
      const percent = Math.round((totalScore / maxScore) * 100)
      const tier = getTier(totalScore)
      const categoryBreakdown = getCategoryScores(answers, questions)

      // Build category scores map (category -> { score, max, percent })
      const categoryScores: Record<
        string,
        { score: number; max: number; percent: number }
      > = {}
      for (const c of categoryBreakdown) {
        categoryScores[c.category] = {
          score: c.score,
          max: c.max,
          percent: c.percent,
        }
      }

      const now = new Date().toISOString()
      const userAgent =
        typeof navigator !== 'undefined' ? navigator.userAgent : ''

      const payload = {
        name: lead.name.trim(),
        email: lead.email.trim().toLowerCase(),
        company: lead.company.trim(),
        role: lead.role.trim(),
        consent: Boolean(lead.consent),
        answers,
        score: totalScore,
        maxScore,
        percent,
        tier: tier.level,
        categoryScores,
        source: 'website',
        userAgent,
        creator: 'anonymous',
        createdAt: now,
        updatedAt: now,
      }

      const created = await lumi.entities.quiz_submissions.create(payload)
      console.log('quiz submission saved:', created?._id)
      return { ok: true, _id: created?._id }
    } catch (e: any) {
      const msg =
        (e && (e.message || e.toString())) || 'Failed to save submission'
      console.error('quiz submission failed:', e)
      setError(msg)
      return { ok: false, error: msg }
    } finally {
      setSubmitting(false)
    }
  }

  return { submit, submitting, error }
}
